import {
  ALLOWED_ATTACHMENT_EXTENSIONS_LABEL,
  MAX_MESSAGE_ATTACHMENT_COUNT,
  MAX_MESSAGE_ATTACHMENTS_TOTAL_SIZE,
} from "@/utils/documents";

export const SOCKET_EVENTS = {
  ERROR: "error",
  SEND_MESSAGE: "send-message",
  TYPING: "typing",
  TYPING_ENDED: "typing-ended",
  JOIN_CONVERSATION: "join-conversation",
  LEAVE_CONVERSATION: "leave-conversation",
  READ_MESSAGES: "read-messages",
} as const;

// Events the client raises on its own - joining rooms on connect, typing
// indicators, read receipts. A failure there is not something the user asked
// for and must not interrupt them with a toast; it is only reported for the
// actions they actually took.
export const BACKGROUND_SOCKET_EVENTS: string[] = [
  SOCKET_EVENTS.TYPING,
  SOCKET_EVENTS.TYPING_ENDED,
  SOCKET_EVENTS.JOIN_CONVERSATION,
  SOCKET_EVENTS.LEAVE_CONVERSATION,
  SOCKET_EVENTS.READ_MESSAGES,
];

// Marks a message, and the attachments on it, that exist only on this client
// until the server answers. Nothing with this prefix is a real id.
export const OPTIMISTIC_ID_PREFIX = "temp-";

// How long a send stays eligible to be matched to a server answer. Nothing on
// the wire correlates an answer to the send that caused it, so sends are
// matched in order; anything older than this can no longer have an answer in
// flight and is dropped rather than matched to someone else's.
export const PENDING_MESSAGE_TTL_MS = 60_000;

// User-facing copy for the structured error codes the socket can return. The
// server's own message is used for anything not listed here, with a generic
// fallback when it sends none.
export const SOCKET_ERROR_MESSAGES: Record<
  string,
  { title: string; description: string }
> = {
  INVALID_ATTACHMENT: {
    title: "Invalid file",
    description: `The file could not be attached. Only ${ALLOWED_ATTACHMENT_EXTENSIONS_LABEL} files are supported, and the file must not be corrupted.`,
  },
  ATTACHMENT_TYPE_MISMATCH: {
    title: "Invalid file",
    description:
      "The file's contents do not match its extension. Re-save it in its proper format and try again.",
  },
  CHAT_ATTACHMENTS_TOO_LARGE: {
    title: "Attachments too large",
    description: `Attachments must total less than ${Math.floor(
      MAX_MESSAGE_ATTACHMENTS_TOTAL_SIZE / (1024 * 1024)
    )}MB across the whole message.`,
  },
  TOO_MANY_ATTACHMENTS: {
    title: "Too many attachments",
    description: `A message can carry at most ${MAX_MESSAGE_ATTACHMENT_COUNT} files.`,
  },
  CONVERSATION_NOT_FOUND: {
    title: "Chat unavailable",
    description:
      "This chat no longer exists, or you no longer have access to it.",
  },
  NOT_FOUND: {
    title: "Chat unavailable",
    description:
      "This chat no longer exists, or you no longer have access to it.",
  },
  NOT_AUTHORIZED: {
    title: "Not allowed",
    description: "You do not have permission to do that.",
  },
  INVALID_DATA_SENT: {
    title: "Message not sent",
    description: "The message could not be sent. Check its length and content.",
  },
  ARCHIVED_ACCOUNT: {
    title: "Account deactivated",
    description: "This account can no longer send messages.",
  },
  UNVERIFIED_ACCOUNT: {
    title: "Account not verified",
    description: "Verify your account before sending messages.",
  },
  S3_ERROR: {
    title: "Upload failed",
    description: "The attachment could not be stored. Please try again.",
  },
};

export const DEFAULT_SOCKET_ERROR = {
  title: "Something went wrong",
  description: "The action could not be completed. Please try again.",
};